// Global packages and components
// @REF: https://www.sanity.io/docs/portable-text-to-react
// @NOTE: Types are a bit confusing here, so using "any" for now
import { Link } from '@remix-run/react'
import { PortableText } from '@portabletext/react'
import type { PortableTextBlock } from '@portabletext/react'
import { headingSizes } from '~/components/partials/common/heading'

interface ContentEditorProps {
  /**
   * Portable text content blocks
   */
  content?: PortableTextBlock[]
}

// Main component
const ContentEditor = ({ content }: ContentEditorProps) => {
  if (!content) return null

  const serializers = {
    types: {
      image: ({ value }: any) => {
        if (!value?.asset?.url) return null

        return (
          <img
            src={value.asset.url}
            alt={value?.alt}
            className="w-full"
          />
        )
      },
    },
    block: {
      blockquote: ({ children }: any) => {
        return (
          <blockquote className="relative mb-4 pl-4 text-lg italic before:absolute before:left-0 before:top-0 before:h-full before:border-l-2 before:border-teal-500">
            {children}
          </blockquote>
        )
      },
      h1: ({ children }: any) => {
        return (
          <h1 className="font-fourm text-5xl/snug md:text-6xl/tight xl:text-7xl/none">
            {children}
          </h1>
        )
      },
      h2: ({ children }: any) => {
        return (
          <h2 className="font-fourm text-3xl/snug md:text-4xl/tight xl:text-5xl/none">
            {children}
          </h2>
        )
      },
      h3: ({ children }: any) => {
        return (
          <h3 className="font-fourm text-2xl/snug md:text-3xl/tight xl:text-4xl/none">
            {children}
          </h3>
        )
      },
      h4: ({ children }: any) => {
        return (
          <h4 className="font-fourm text-xl/snug md:text-2xl/tight xl:text-3xl/none">
            {children}
          </h4>
        )
      },
      h5: ({ children }: any) => {
        return (
          <h5 className="font-fourm text-lg/snug md:text-xl/tight xl:text-2xl/none">
            {children}
          </h5>
        )
      },
      h6: ({ children }: any) => {
        return (
          <h6 className="font-fourm text-base/snug md:text-lg/tight xl:text-xl/none">
            {children}
          </h6>
        )
      },
      normal: ({ children }: any) => {
        return <p>{children}</p>
      },
      normalSm: ({ children }: any) => {
        return <p className="text-sm">{children}</p>
      },
      normalLg: ({ children }: any) => {
        return <p className="text-lg">{children}</p>
      },
    },
    marks: {
      strong: ({ children }: any) => {
        return <span className="font-bold">{children}</span>
      },
      semibold: ({ children }: any) => {
        return <span className="font-semibold">{children}</span>
      },
      uppercase: ({ children }: any) => {
        return <span className="uppercase">{children}</span>
      },
      textWhite: ({ children }: any) => {
        return <span className="text-white">{children}</span>
      },
      textGrey: ({ children }: any) => {
        return <span className="text-gray-500">{children}</span>
      },
      textDark: ({ children }: any) => {
        return <span className="text-gray-900">{children}</span>
      },
      textDarkTeal: ({ children }: any) => (
        <span style={{ color: '#173138' }}>{children}</span>
      ),
      textTeal: ({ children }: any) => (
        <span style={{ color: '#244A53' }}>{children}</span>
      ),
      textLightTeal: ({ children }: any) => (
        <span style={{ color: '#66909B' }}>{children}</span>
      ),
      textGold: ({ children }: any) => (
        <span style={{ color: '#D0A94C' }}>{children}</span>
      ),
      textLightGold: ({ children }: any) => (
        <span style={{ color: '#E2BC62' }}>{children}</span>
      ),
      textBeige: ({ children }: any) => (
        <span style={{ color: '#EAE8E4' }}>{children}</span>
      ),
      textLightBeige: ({ children }: any) => (
        <span style={{ color: '#F2F0EC' }}>{children}</span>
      ),
      sizeH1: ({ children }: any) => {
        return <span className={headingSizes.h1}>{children}</span>
      },
      sizeH2: ({ children }: any) => {
        return <span className={headingSizes.h2}>{children}</span>
      },
      sizeH3: ({ children }: any) => {
        return <span className={headingSizes.h3}>{children}</span>
      },
      sizeH4: ({ children }: any) => {
        return <span className={headingSizes.h4}>{children}</span>
      },
      internalLink: ({ value, children }: any) => {
        if (!value?.slug?.current) return null

        return (
          <Link
            to={value.slug.current}
            className="underline"
          >
            {children}
          </Link>
        )
      },
      link: ({ value, children }: any) => {
        if (!value?.href) return null

        return (
          <a
            href={value.href}
            target={value?.blank ? '_blank' : '_self'}
            rel={value?.blank ? 'noreferrer' : ''}
          >
            {children}
          </a>
        )
      },
    },
    list: {
      bullet: ({ children }: any) => (
        <ul className="mb-4 ml-5 list-disc">{children}</ul>
      ),
      number: ({ children }: any) => (
        <ol className="mb-4 ml-5 list-decimal">{children}</ol>
      ),
    },
  }

  return (
    <PortableText
      value={content}
      components={serializers}
    />
  )
}

export default ContentEditor
